import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { EngineSettings } from "@/api/filth-settings-api"

import { loadEngineSettings, saveEngineSettings } from "@/api/filth-settings-api"
import { Server } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { FilthSearchSettings } from "./filth-search-settings"
import {
  costConfirmation,
  ENGINE_BOUNDS,
  engineBoundsLabel,
  engineChanges,
  engineErrorMessage,
  type SearchSettingsControls,
  validateEngineDraft,
} from "./filth-settings-utils"

type PendingCost = {
  estimate: {
    cost_estimate: Record<string, string | number>
    estimate_id: string
    expires_at: number
  }
  revision: number
  changes: Record<string, string | number>
}

export function FilthEngineSettings({
  id,
  combined = false,
  onSearchOverridesChange,
  onOverridesChange,
}: {
  id: string
  combined?: boolean
  onSearchOverridesChange?: (count: number) => void
  onOverridesChange?: (count: number) => void
}) {
  const { t, i18n } = useTranslation()
  const searchControls = useRef<SearchSettingsControls>(null)
  const [searchState, setSearchState] = useState({ dirty: false, valid: false, loaded: false })
  const [saveResult, setSaveResult] = useState<string | null>(null)
  const [settings, setSettings] = useState<EngineSettings | null>(null)
  const [draft, setDraft] = useState<Record<string, string | number>>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pendingCost, setPendingCost] = useState<PendingCost | null>(null)
  const [pendingEffect, setPendingEffect] = useState<unknown>(null)
  useEffect(() => {
    let active = true
    setSettings(null)
    setError(null)
    setPendingCost(null)
    setPendingEffect(null)
    void loadEngineSettings(id)
      .then((data) => {
        if (active) {
          setSettings(data)
          setDraft(Object.fromEntries(data.settings.map((item) => [item.key, item.value])))
        }
      })
      .catch((err: unknown) => {
        if (active) setError(engineErrorMessage(err, t))
      })
    return () => {
      active = false
    }
  }, [id, t])
  const reload = async () => {
    setBusy(true)
    setPendingCost(null)
    setSaveResult(null)
    try {
      if (combined) await searchControls.current?.reload()
      const data = await loadEngineSettings(id)
      setSettings(data)
      setDraft(Object.fromEntries(data.settings.map((item) => [item.key, item.value])))
      setError(null)
      setPendingEffect(null)
    } catch (err) {
      setError(engineErrorMessage(err, t))
    } finally {
      setBusy(false)
    }
  }
  const changes = settings ? engineChanges(settings.settings, draft) : {}
  const overrideCount =
    settings?.settings.filter(
      (item) =>
        draft[item.key] !== item.default &&
        (!combined ||
          (item.key !== "serving.context_entity_arm" &&
            item.key !== "serving.context_entity_weight"))
    ).length ?? 0
  useEffect(() => {
    onOverridesChange?.(overrideCount)
  }, [overrideCount, onOverridesChange])
  const validation = validateEngineDraft(changes, t)
  const save = async (confirmed?: PendingCost) => {
    if (busy) return
    const submitted =
      confirmed ?? (settings && !validation ? { revision: settings.revision, changes } : null)
    setBusy(true)
    if (settings) setError(null)
    setSaveResult(null)
    if (submitted && Object.keys(submitted.changes).length) setPendingEffect(null)
    let engineResult = Object.keys(changes).length ? "notSaved" : "unchanged"
    if (!settings) engineResult = "notLoaded"
    let awaitingCost = false
    try {
      if (submitted && Object.keys(submitted.changes).length) {
        const response = await saveEngineSettings(id, {
          revision: submitted.revision,
          changes: submitted.changes,
          ...(confirmed ? { confirm_cost: true, estimate_id: confirmed.estimate.estimate_id } : {}),
        })
        setPendingCost(null)
        setPendingEffect(response.pending_effect)
        // The new revision is authoritative; a reload failure must not hide a successful write.
        setSettings(
          (current) =>
            current && {
              revision: response.revision,
              settings: current.settings.map((item) => ({
                ...item,
                value: response.applied[item.key] ?? item.value,
              })),
            }
        )
        setDraft((current) => ({ ...current, ...response.applied }))
        engineResult = "saved"
      }
    } catch (err) {
      const estimate = costConfirmation(err)
      if (estimate !== null && submitted) {
        setPendingCost({ ...submitted, estimate })
        awaitingCost = true
      } else {
        setPendingCost(null)
        setError(engineErrorMessage(err, t))
        if (
          (err as { response?: { data?: { result?: string } } }).response?.data?.result ===
          "applied_unverified"
        )
          engineResult = "unverified"
      }
    } finally {
      if (combined && !awaitingCost) {
        let searchResult = searchState.dirty ? "notSaved" : "unchanged"
        if (!searchState.loaded) searchResult = "notLoaded"
        if (searchState.dirty && searchState.valid)
          searchResult = (await searchControls.current?.save()) ? "saved" : "notSaved"
        setSaveResult(
          t("adminDashboard.filthSettings.combinedSaveResult", {
            engine: t(`adminDashboard.filthSettings.saveResults.${engineResult}`),
            search: t(`adminDashboard.filthSettings.saveResults.${searchResult}`),
          })
        )
      }
      setBusy(false)
    }
  }
  const renderItem = (item: EngineSettings["settings"][number]) => {
    const fieldId = `engine-${id}-${item.key}`
    const bounds = ENGINE_BOUNDS[item.key]
    const value = draft[item.key]
    const displayBounds = engineBoundsLabel(item.bounds, t)
    return (
      <div
        key={item.key}
        className="grid min-w-0 items-start gap-4 border-t pt-4 sm:grid-cols-[minmax(0,1fr)_20rem]"
      >
        <div className="min-w-0 space-y-3">
          <Label htmlFor={fieldId}>
            {t(`adminDashboard.filthSettings.fields.${item.key}`, {
              defaultValue: t("adminDashboard.filthSettings.unknownSetting"),
            })}
          </Label>
          <p
            className="text-muted-foreground max-w-prose text-sm leading-relaxed"
            id={`${fieldId}-help`}
          >
            {t(`adminDashboard.filthSettings.fieldDescriptions.${item.key}`)}
          </p>
          <div className="flex flex-wrap gap-2">
            {value !== item.default && (
              <Badge variant="outline">{t("adminDashboard.filthSettings.overridden")}</Badge>
            )}
            {item.effect === "rederive" && (
              <Badge variant="warning">{t("adminDashboard.filthSettings.reindexBadge")}</Badge>
            )}
            {item.kill_switch_active && (
              <Badge variant="warning">{t("adminDashboard.filthSettings.killSwitch")}</Badge>
            )}
          </div>
        </div>
        {bounds ? (
          <Input
            aria-describedby={`${fieldId}-help`}
            className="min-h-10 w-full"
            disabled={busy || pendingCost !== null || item.kill_switch_active}
            id={fieldId}
            max={bounds[1]}
            min={bounds[0]}
            step={bounds[2] ? 1 : "any"}
            type="number"
            value={typeof value === "number" && Number.isNaN(value) ? "" : value}
            onChange={(event) => {
              setPendingCost(null)
              setDraft((current) => ({
                ...current,
                [item.key]: event.target.value === "" ? NaN : Number(event.target.value),
              }))
            }}
          />
        ) : (
          <Select
            disabled={busy || pendingCost !== null || item.kill_switch_active}
            value={String(value)}
            onValueChange={(next) => {
              setPendingCost(null)
              setDraft((current) => ({ ...current, [item.key]: next }))
            }}
          >
            <SelectTrigger
              aria-describedby={`${fieldId}-help`}
              className="min-h-10 w-full"
              id={fieldId}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {item.kill_switch_active && (
                <SelectItem disabled value="disabled">
                  {t("adminDashboard.filthSettings.disabled")}
                </SelectItem>
              )}
              <SelectItem value="off">{t("adminDashboard.filthSettings.off")}</SelectItem>
              <SelectItem value="on">{t("adminDashboard.filthSettings.on")}</SelectItem>
            </SelectContent>
          </Select>
        )}
        {value !== item.default && (
          <Button
            className="sm:col-start-2"
            disabled={busy || pendingCost !== null || item.kill_switch_active}
            type="button"
            variant="outline"
            onClick={() => {
              setPendingCost(null)
              setDraft((current) => ({ ...current, [item.key]: item.default }))
            }}
          >
            {t("adminDashboard.filthSettings.resetDefault")}
          </Button>
        )}
        <details className="text-muted-foreground text-sm sm:col-span-2">
          <summary className="cursor-pointer">{t("adminDashboard.filthSettings.details")}</summary>
          <p className="max-w-prose pt-3 leading-relaxed">
            {t("adminDashboard.filthSettings.engineValues", {
              stored: String(item.value),
              default: String(item.default),
              effective: item.kill_switch_active
                ? t("adminDashboard.filthSettings.disabledByOperator")
                : String(item.value),
              bounds: displayBounds,
            })}
            {item.cost_hint !== undefined && item.cost_hint !== "none"
              ? t("adminDashboard.filthSettings.costHint", {
                  cost: t("adminDashboard.filthSettings.paidWorkHint"),
                })
              : ""}
          </p>
        </details>
      </div>
    )
  }
  const searchQuality = combined ? (
    <FilthSearchSettings
      embedded
      controlsRef={searchControls}
      disabled={busy || pendingCost !== null}
      engineSettings={settings}
      id={id}
      candidateControls={settings?.settings
        .filter((item) => item.key === "serving.context_entity_max_candidates")
        .map(renderItem)}
      onOverridesChange={onSearchOverridesChange}
      onStateChange={setSearchState}
    />
  ) : null
  return (
    <section
      aria-label={t("adminDashboard.filthSettings.engineTitle")}
      className="min-w-0 space-y-6"
    >
      {!searchQuality && (
        <p className="text-sm">
          {t("adminDashboard.filthSettings.overrideSummary", { count: overrideCount })}
        </p>
      )}
      {!settings && !error && (
        <p className="text-muted-foreground rounded-md border p-4 text-sm" role="status">
          {t("adminDashboard.filthSettings.loadingEngine")}
        </p>
      )}
      {settings?.settings.length === 0 && (
        <div className="space-y-3 rounded-lg border p-6" role="status">
          <Server aria-hidden="true" className="bg-muted size-12 rounded-lg border p-3" />
          <h5 className="font-display">{t("adminDashboard.filthSettings.noEngineSettings")}</h5>
          <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
            {t("adminDashboard.filthSettings.noEngineSettingsDescription")}
          </p>
        </div>
      )}
      {["serving", "crawl"].map((group) => {
        const items = settings?.settings.filter((item) => item.key.startsWith(`${group}.`)) ?? []
        if (!items.length && !(group === "serving" && searchQuality)) return null
        return (
          <details key={group} open className="bg-card space-y-6 rounded-lg border p-6">
            <summary className="cursor-pointer space-y-2">
              <h3 className="font-display inline text-xl">
                {t(`adminDashboard.filthSettings.engineGroups.${group}`)}
              </h3>
              <span className="text-muted-foreground mt-2 block max-w-prose text-sm leading-relaxed">
                {t(`adminDashboard.filthSettings.groupDescriptions.${group}`)}
              </span>
            </summary>
            {group === "serving" && searchQuality}
            {items
              .filter((item) => !combined || !item.key.startsWith("serving.context_entity_"))
              .map(renderItem)}
          </details>
        )
      })}
      {(error !== null || validation !== null) && (
        <p
          className="bg-destructive text-destructive-foreground border-foreground rounded-md border p-4 text-sm leading-relaxed"
          role="alert"
        >
          {error || validation}
        </p>
      )}
      {pendingEffect !== null && (
        <p className="rounded-md border p-4 text-sm leading-relaxed" role="status">
          {t(
            combined
              ? "adminDashboard.filthSettings.enginePendingEffect"
              : "adminDashboard.filthSettings.pendingEffect",
            {
              effect: t(`adminDashboard.filthSettings.effects.${String(pendingEffect)}`),
            }
          )}
        </p>
      )}
      {saveResult && (
        <p className="rounded-md border p-4 text-sm leading-relaxed" role="status">
          {saveResult}
        </p>
      )}
      <div className="flex flex-wrap gap-3 border-t pt-6">
        <Button
          type="button"
          disabled={
            busy ||
            pendingCost !== null ||
            !(
              (settings && !validation && Object.keys(changes).length) ||
              (combined && searchState.dirty && searchState.valid)
            )
          }
          onClick={() => void save()}
        >
          {t("adminDashboard.filthSettings.saveEngine")}
        </Button>
        <Button disabled={busy} type="button" variant="outline" onClick={() => void reload()}>
          {t(
            combined
              ? "adminDashboard.filthSettings.reloadAll"
              : "adminDashboard.filthSettings.reload"
          )}
        </Button>
      </div>
      <AlertDialog
        open={pendingCost !== null}
        onOpenChange={(open) => {
          if (!open) setPendingCost(null)
        }}
      >
        <AlertDialogContent className="max-h-[90vh] gap-6 overflow-y-auto p-6 sm:max-w-2xl sm:p-8">
          <AlertDialogHeader className="gap-3">
            <AlertDialogTitle>{t("adminDashboard.filthSettings.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="max-w-prose leading-relaxed">
              {t("adminDashboard.filthSettings.confirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="min-w-0 space-y-6 text-sm">
            <div>
              <h4 className="font-display mb-4 text-base">
                {t("adminDashboard.filthSettings.changesTitle")}
              </h4>
              <dl className="divide-y rounded-lg border px-4">
                {Object.entries(pendingCost?.changes ?? {}).map(([key, value]) => (
                  <div
                    key={key}
                    className="grid min-w-0 gap-2 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-6"
                  >
                    <dt className="min-w-0 space-y-2">
                      <span>
                        {t(`adminDashboard.filthSettings.fields.${key}`, {
                          defaultValue: t("adminDashboard.filthSettings.unknownSetting"),
                        })}
                      </span>
                    </dt>
                    <dd className="min-w-0 font-medium break-words sm:text-right">
                      {typeof value === "string"
                        ? t(`adminDashboard.filthSettings.${value}`)
                        : value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <h4 className="font-display mb-4 text-base">
                {t("adminDashboard.filthSettings.estimateTitle")}
              </h4>
              <p className="text-muted-foreground mb-4 max-w-prose leading-relaxed">
                {typeof pendingCost?.estimate.cost_estimate.estimated_minutes === "number" &&
                  t("adminDashboard.filthSettings.estimateWork", {
                    hours: new Intl.NumberFormat(i18n.resolvedLanguage, {
                      maximumFractionDigits: 1,
                    }).format(pendingCost.estimate.cost_estimate.estimated_minutes / 60),
                  })}
                {typeof pendingCost?.estimate.cost_estimate.estimated_note_calls === "number" &&
                  ` ${t("adminDashboard.filthSettings.estimateCalls", { calls: new Intl.NumberFormat(i18n.resolvedLanguage).format(pendingCost.estimate.cost_estimate.estimated_note_calls) })}`}
              </p>
              <dl className="divide-y rounded-lg border px-4">
                {Object.entries(pendingCost?.estimate.cost_estimate ?? {}).map(([key, value]) => (
                  <div
                    key={key}
                    className="grid min-w-0 gap-2 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-6"
                  >
                    <dt className="min-w-0 space-y-2 break-words">
                      {t(`adminDashboard.filthSettings.estimateFields.${key}`, {
                        defaultValue: t("adminDashboard.filthSettings.unknownEstimate"),
                      })}
                    </dt>
                    <dd className="min-w-0 font-medium break-words tabular-nums sm:text-right">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>
              {t("adminDashboard.filthSettings.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() => {
                if (pendingCost) void save(pendingCost)
              }}
            >
              {t("adminDashboard.filthSettings.confirmApply")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
